<template>
  <div class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-hidden">
    <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between">
        <div>
          <div class="flex items-center gap-2">
            <ChartBarIcon class="h-5 w-5 text-blue-500" />
            <h3 class="text-lg font-medium text-gray-900 dark:text-white">
              {{ chartTitle }}
            </h3>
            <button
              @click="showInfo = !showInfo"
              class="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
              title="Uitleg over de omzetgrafiek"
            >
              <InformationCircleIcon class="h-5 w-5" />
            </button>
          </div>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {{ chartSubtitle }}
          </p>
        </div>
        <button
          v-if="drilldownLevel !== 'overview'"
          @click="goBack"
          class="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors"
        >
          <ArrowLeftIcon class="h-4 w-4" />
          Terug
        </button>
      </div>
    </div>

    <InfoBox v-if="showInfo" title="" color="blue">
      <p>
        De grafiek toont de totale omzet, uitgesplitst in geschreven en gesourcete uren (groen),
        derdenomzet (blauw) en de salesfunnel (oranje en geel).
      </p>
      <p>
        De bronnen zijn de timesheets en de vaste prijs diensten uit Simplicate, de geconcretiseerde
        saleskansen uit de sourcingstool en de CRM module uit Simplicate.
      </p>
    </InfoBox>

    <div class="p-5">
      <VChart
        :option="chartOption"
        :notMerge="true"
        autoresize
        style="width: 100%; height: 350px"
        @click="handleClick"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, toRef, onMounted, onUnmounted } from 'vue'
import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { ChartBarIcon, ArrowLeftIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'
import InfoBox from './InfoBox.vue'
import { useChartDrilldown } from '@/composables/charts/useChartDrilldown.js'
import { useHoursRevenueChartConfig } from '@/composables/charts/useHoursRevenueChartConfig.js'

use([BarChart, LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

const props = defineProps({
  data: { type: Object, required: true },
  derdenOmzetData: { type: Object, default: null },
  budgetData: { type: Array, default: () => [] },
  pipelineData: { type: Object, default: null }
})

const showInfo = ref(false)
const isDarkMode = ref(false)

const updateDarkMode = () => {
  isDarkMode.value = document.documentElement.classList.contains('dark')
}

let observer = null

onMounted(() => {
  updateDarkMode()
  observer = new MutationObserver(updateDarkMode)
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const {
  drilldownLevel,
  drilldownTeam,
  drilldownPipelineCategory,
  chartTitle,
  chartSubtitle,
  goBack
} = useChartDrilldown()

const { chartOption, handleClick } = useHoursRevenueChartConfig({
  data: toRef(props, 'data'),
  derdenOmzetData: toRef(props, 'derdenOmzetData'),
  budgetData: toRef(props, 'budgetData'),
  pipelineData: toRef(props, 'pipelineData'),
  drilldownLevel,
  drilldownTeam,
  drilldownPipelineCategory,
  isDarkMode
})
</script>
